import { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import "react-virtualized/styles.css";
import { getBadgeProps, getCategoryBadgeProps } from "../utils/common-utils";
import { useApi } from "../context/apiContext";
import { transformData, transformEventData } from "../utils/data-transform";
import usePopup from "../hooks/usePopup";
import { FaChevronDown, FaEllipsisH, FaFileDownload } from "react-icons/fa";
import Popup from "./PopUp";

const BATCH_SIZE = 25;

const TableResultsComponent = ({
  submittedSearchTerm,
  path,
  ssp: _ssp,
  token,
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [batchStartPage, setBatchStartPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { apiType, apiClient } = useApi();
  const [isTableData, setIsTableData] = useState(false);
  const [columnKeys, setColumnKeys] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const {
    isPopupOpen,
    popupDetails,
    handleOpenPopup,
    handleClosePopup,
    popupLoading,
    spatialExtent,
  } = usePopup(headers);

  const toggleDescription = (index) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const requestData = {
        query: submittedSearchTerm,
        page: batchStartPage,
        size: BATCH_SIZE,
        count: totalRecords || 0,
      };
      // console.log('Token before API call:', token)
      const response = await apiClient.postRequest(
        path,
        requestData,
        apiType,
        headers,
      );
      if (response.pageinfo.count === 0) {
        setError(
          "No results have been retrieved for specified search criteria. Please modify your search criteria and try again.",
        );
        setTotalRecords(0);
        setHasMore(false);
      } else if (response.tabular_data?.length > 0) {
        setTotalRecords(response.pageinfo.count);
        const newData = response.tabular_data;
        setRecords((prev) => [...prev, ...newData]);
        const keys = Object.keys(response.tabular_data[0]);
        setColumnKeys(keys);
        setIsTableData(true);
        const totalLoaded = records.length + newData.length;
        setHasMore(totalLoaded < response.pageinfo.count);
      } else {
        const transformedData = path.includes("/event")
          ? transformEventData(response)
          : transformData(response);
        setTotalRecords(response.pageinfo.count);
        setRecords((prev) => [...prev, ...transformedData]);
        setIsTableData(false);
        const totalLoaded = records.length + transformedData.length;
        setHasMore(totalLoaded < response.pageinfo.count);
      }
    } catch (error) {
      setError(
        "We're having trouble retrieving results for your search. Please check your input and try again.",
      );
      console.error("Error fetching data:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
    // headers is derived from token; token is already in the dependency list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    batchStartPage,
    submittedSearchTerm,
    apiClient,
    path,
    apiType,
    token,
    records.length,
    totalRecords,
  ]);

  const lastFetchedTermRef = useRef("");
  const lastBatchPageRef = useRef(0);

  useEffect(() => {
    if (submittedSearchTerm !== lastFetchedTermRef.current) {
      setRecords([]);
      setBatchStartPage(1);
      setHasMore(true);
      setError(null);
      lastBatchPageRef.current = 0;
    }
    if (
      submittedSearchTerm &&
      submittedSearchTerm !== "null" &&
      (submittedSearchTerm !== lastFetchedTermRef.current ||
        batchStartPage !== lastBatchPageRef.current)
    ) {
      fetchData();
      lastFetchedTermRef.current = submittedSearchTerm;
      lastBatchPageRef.current = batchStartPage;
    }
  }, [batchStartPage, submittedSearchTerm, fetchData]);

  const loadMoreRecords = () => {
    if (loading || !hasMore) return;
    setBatchStartPage((prev) => prev + 1);
  };

  const handleDownloadCSV = async () => {
    try {
      const requestData = {
        query: submittedSearchTerm,
        page: 1,
        size: 10000,
        count: totalRecords || 0,
      };
      const response = await apiClient.postRequest(
        `${path}/csv`,
        requestData,
        apiType,
        headers,
      );

      // If the response is a CSV string, trigger download
      if (typeof response === "string") {
        console.log("Response########################:", response);
        const blob = new Blob([response], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "models.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up
      } else {
        console.error("Unexpected response format:", response);
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const renderTableHeaders = () => {
    if (isTableData) {
      return columnKeys.map((key) => (
        <div
          key={key}
          className="table-header"
          style={{ width: `${100 / columnKeys.length}%` }}
        >
          {key.replaceAll("_", " ")}
        </div>
      ));
    } else {
      return (
        <>
          <div className="table-header" style={{ width: "30%" }}>
            Name
          </div>
          <div className="table-header" style={{ width: "15%" }}>
            HUC
          </div>
          <div className="table-header" style={{ width: "40%" }}>
            Description
          </div>
          <div className="table-header" style={{ width: "15%" }}>
            Actions
          </div>
        </>
      );
    }
  };

  const renderTableRows = () => {
    if (isTableData) {
      return records.map((record, index) => (
        <div key={`record-${index}`} className="table-row">
          {columnKeys.map((key) => (
            <div
              key={`${index}-${key}`}
              className="table-cell"
              style={{ width: `${100 / columnKeys.length}%` }}
            >
              {record[key] || "N/A"}
            </div>
          ))}
        </div>
      ));
    } else {
      return records.map((record, index) => {
        const categoryBadge = getCategoryBadgeProps(record.model_category);
        return (
          <div key={`record-${index}`} className="table-row">
            <div className="table-cell name-cell" style={{ width: "30%" }}>
              <span className="name-text-full">
                {record.name}
                <sup
                  className="source-superscript"
                  style={{
                    color:
                      getBadgeProps(record.source_application).textColor ||
                      "#fff",
                    backgroundColor:
                      getBadgeProps(record.source_application)
                        .backgroundColor || "#6c757d",
                  }}
                >
                  {record.source_application
                    ? record.source_application.toUpperCase()
                    : ""}
                </sup>

                {/* NEW CATEGORY BADGE (Alternative Analysis / Baseline) */}
                {categoryBadge && (
                  <span
                    style={{
                      backgroundColor: categoryBadge.bg,
                      color: categoryBadge.textColor || "#fff",
                      marginLeft: "6px",
                      padding: "2px 12px",
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      display: "inline-block",
                      verticalAlign: "super",
                      letterSpacing: "1px",
                    }}
                  >
                    {categoryBadge.label}
                  </span>
                )}
              </span>
            </div>
            <div className="table-cell huc-cell" style={{ width: "15%" }}>
              {!record.huc_code ? (
                <div className="huc-na">
                  N/A<span className="red-asterisk">*</span>
                </div>
              ) : (
                <div className="huc-code">{record.huc_code}</div>
              )}
            </div>
            <div
              className="table-cell description-cell"
              style={{ width: "40%" }}
            >
              <button
                type="button"
                className={`description-text ${
                  expandedDescriptions[index] ? "expanded" : ""
                }`}
                onClick={() => toggleDescription(index)}
                aria-expanded={expandedDescriptions[index]}
                aria-label={`${expandedDescriptions[index] ? "Collapse" : "Expand"} description`}
              >
                {record.description}
              </button>
              {!expandedDescriptions[index] &&
                record.description &&
                record.description.length > 150 && (
                  <button
                    className="read-more-btn"
                    onClick={() => toggleDescription(index)}
                  >
                    Read more <FaEllipsisH />
                  </button>
                )}
            </div>
            <div className="table-cell actions-cell" style={{ width: "15%" }}>
              <button
                className="preview-download-btn"
                onClick={() => handleOpenPopup(record)}
              >
                <span className="btn-text-full">Preview & Download</span>
                <span className="btn-text-short">Pvw & Dwnld</span>
              </button>
            </div>
          </div>
        );
      });
    }
  };

  return (
    <div className="query-results-1">
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      <div className="enhanced-table-container">
        <div
          className="table-actions"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <button
            className="download-all-btn"
            onClick={handleDownloadCSV}
            title="Download all results as CSV"
            style={{ fontSize: "0.9rem" }}
          >
            <FaFileDownload /> Export As CSV
          </button>
        </div>
        <div className="table-wrapper">
          <div className="table-header-row">{renderTableHeaders()}</div>
          <div className="table-body">{renderTableRows()}</div>
          {hasMore && (
            <div className="table-footer">
              <button
                className="load-more-btn"
                onClick={loadMoreRecords}
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner-small"></div>
                ) : (
                  <>
                    Load More <FaChevronDown />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      {popupLoading && (
        <div className="loading-indicator">
          <p>Loading preview data</p>
          <div className="dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}
      {!popupLoading && isPopupOpen && (
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          details={popupDetails}
          headers={headers}
          spatialExtent={spatialExtent}
        />
      )}
    </div>
  );
};

TableResultsComponent.propTypes = {
  submittedSearchTerm: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  path: PropTypes.string,
  ssp: PropTypes.bool,
  token: PropTypes.string,
};

export default TableResultsComponent;
